import React, { useState, useEffect } from 'react';
import { 
  CloudArrowUpIcon,
  DocumentTextIcon,
  VideoCameraIcon,
  TrashIcon,
  EyeIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import apiService from '../services/api';

const MaterialManagement = () => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedMaterialType, setSelectedMaterialType] = useState('video');
  const [materials, setMaterials] = useState([]);
  const [courses, setCourses] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    course: '',
    module: '',
    type: 'video',
    description: '',
    notes: '',
    file: null,
    youtube_url: '',
    order: 1,
    is_public: false
  });

  useEffect(() => {
    loadMaterials();
    loadCourses();
  }, []);

  const loadMaterials = async () => {
    try {
      setLoading(true);
      const materialsData = await apiService.getMaterials();
      setMaterials(Array.isArray(materialsData) ? materialsData : []);
      setError('');
    } catch (error) {
      console.error('Failed to load materials:', error);
      setError('Failed to load materials. Please try again.');
      setMaterials([]); // Ensure materials is always an array
    } finally {
      setLoading(false);
    }
  };

  const loadCourses = async () => {
    try {
      const coursesData = await apiService.getCourses();
      setCourses(Array.isArray(coursesData) ? coursesData : []);
    } catch (error) {
      console.error('Failed to load courses:', error);
    }
  };

  const loadModules = async (courseId) => {
    if (!courseId) {
      setModules([]);
      return;
    }
    try {
      const modulesData = await apiService.getCourseModules(courseId);
      setModules(Array.isArray(modulesData) ? modulesData : []);
    } catch (error) {
      console.error('Failed to load modules:', error);
      setModules([]);
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    if (type === 'file') {
      setFormData(prev => ({ ...prev, [name]: files[0] || null }));
    } else if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
      
      // If course changes, load modules for that course
      if (name === 'course') {
        loadModules(value);
        // Reset module selection when course changes
        setFormData(prev => ({ ...prev, module: '' }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setUploading(true);
      setError('');
      setSuccess('');
      
      // Validate required fields
      if (!formData.title.trim()) {
        setError('Please enter a material title.');
        setUploading(false);
        return;
      }
      
      if (!formData.course) {
        setError('Please select a course.');
        setUploading(false);
        return;
      }
      
      if (selectedMaterialType === 'video' && !formData.youtube_url && !formData.file) {
        setError('Please provide either a video file or YouTube URL.');
        setUploading(false);
        return;
      }
      
      if (selectedMaterialType !== 'video' && !formData.file) {
        setError('Please upload a file.');
        setUploading(false);
        return;
      }
      
      // Prepare material data
      const materialData = {
        title: formData.title,
        material_type: selectedMaterialType,
        description: formData.description || '',
        notes: formData.notes || '',
        order: parseInt(formData.order) || 1,
        is_public: formData.is_public,
      };
      
      // Add course (backend will create default module if module not provided)
      if (formData.course) {
        materialData.course = formData.course;
      }
      
      // Add module if provided, otherwise backend will create default "General Materials" module
      if (formData.module) {
        materialData.module = formData.module;
      }
      
      // Add file or YouTube URL based on type
      if (selectedMaterialType === 'video' && formData.youtube_url) {
        materialData.youtube_url = formData.youtube_url.trim();
      } else if (formData.file) {
        materialData.file = formData.file;
      }
      
      await apiService.createMaterial(materialData);
      
      // Reset form
      setFormData({
        title: '',
        course: '',
        module: '',
        type: 'video',
        description: '',
        notes: '',
        file: null,
        youtube_url: '',
        order: 1,
        is_public: false
      });
      setSelectedMaterialType('video');
      setShowUploadModal(false);
      setSuccess('✅ Material uploaded successfully!');
      
      // Reload materials
      await loadMaterials();
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Failed to upload material:', error);
      setError(error.message || 'Failed to upload material. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'video': return VideoCameraIcon;
      case 'document': return DocumentTextIcon;
      default: return DocumentTextIcon;
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
            Material Management
          </h1>
          <p className="text-gray-600 mt-2">Upload and manage course materials, videos, and documents</p>
        </div>
        <button
          onClick={() => {
            setError('');
            setSuccess('');
            setShowUploadModal(true);
          }}
          className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold py-3 px-6 rounded-2xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-105 flex items-center"
        >
          <CloudArrowUpIcon className="h-5 w-5 mr-2" />
          Upload Material
        </button>
      </div>

      {/* Success Message */}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-800 font-medium">{success}</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800 font-medium">{error}</p>
        </div>
      )}

      {/* Materials Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          <div className="col-span-full text-center py-8">
            <div className="text-gray-500">Loading materials...</div>
          </div>
        ) : error ? (
          <div className="col-span-full text-center py-8">
            <div className="text-red-500">{error}</div>
          </div>
        ) : !Array.isArray(materials) || materials.length === 0 ? (
          <div className="col-span-full text-center py-8">
            <div className="text-gray-500">No materials uploaded yet. Click "Upload Material" to get started.</div>
          </div>
        ) : (
          materials.map((material) => {
            const materialType = material.material_type || material.type || 'document';
            const Icon = getTypeIcon(materialType);
            return (
              <div key={material.id} className="bg-white/80 backdrop-blur-xl border border-white/20 rounded-3xl p-6 shadow-xl shadow-black/5 hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02]">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center mr-3">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900">{material.title}</h3>
                      <p className="text-sm text-gray-600">{material.course_name || material.module_detail?.course?.name || 'No course assigned'}</p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button className="text-gray-400 hover:text-blue-600 transition-colors">
                      <EyeIcon className="w-4 h-4" />
                    </button>
                    <button className="text-gray-400 hover:text-green-600 transition-colors">
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    <button className="text-gray-400 hover:text-red-600 transition-colors">
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2 text-sm text-gray-600">
                  <p><span className="font-medium">Module:</span> {material.module_name || material.module_detail?.name || '-'}</p>
                  <p><span className="font-medium">Type:</span> {materialType.charAt(0).toUpperCase() + materialType.slice(1)}</p>
                  <p><span className="font-medium">Uploaded:</span> {material.created_at ? new Date(material.created_at).toLocaleDateString() : 'Unknown date'}</p>
                  {material.notes && (
                    <div>
                      <p className="font-medium text-gray-700 mb-1">Notes:</p>
                      <p className="text-xs bg-gray-50 p-2 rounded-lg line-clamp-3">{material.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Upload Material Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Upload Course Material</h3>
              <button
                onClick={() => setShowUploadModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Material Title *</label>
                <input 
                  type="text" 
                  name="title"
                  className="input-field" 
                  placeholder="e.g., Introduction to Python Functions"
                  value={formData.title}
                  onChange={handleFormChange}
                  required
                  disabled={uploading}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Course *</label>
                  <select 
                    name="course"
                    className="input-field"
                    value={formData.course}
                    onChange={handleFormChange}
                    required
                    disabled={uploading}
                  >
                    <option value="">Select Course</option>
                    {courses.map((course) => (
                      <option key={course.id} value={course.id}>{course.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Module</label>
                  <select 
                    name="module"
                    className="input-field"
                    value={formData.module}
                    onChange={handleFormChange}
                    disabled={uploading || !formData.course || modules.length === 0}
                  >
                    <option value="">Select Module (Optional)</option>
                    {modules.map((module) => (
                      <option key={module.id} value={module.id}>{module.name}</option>
                    ))}
                  </select>
                  {formData.course && modules.length === 0 && (
                    <p className="text-xs text-gray-500 mt-1">No modules available for this course</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Material Type</label>
                <select 
                  name="type"
                  className="input-field"
                  value={selectedMaterialType}
                  onChange={(e) => {
                    setSelectedMaterialType(e.target.value);
                    setFormData(prev => ({ ...prev, type: e.target.value }));
                  }}
                  disabled={uploading}
                >
                  <option value="video">Video</option>
                  <option value="document">Document</option>
                  <option value="presentation">Presentation</option>
                  <option value="assignment">Assignment</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea 
                  name="description"
                  className="input-field" 
                  rows="3" 
                  placeholder="Brief description of the material..."
                  value={formData.description}
                  onChange={handleFormChange}
                  disabled={uploading}
                ></textarea>
              </div>

              {selectedMaterialType === 'video' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Video Notes/Write-up</label>
                  <textarea 
                    name="notes"
                    className="input-field" 
                    rows="5" 
                    placeholder="Notes or write-up to display under the video. Include key points, exercises, or additional information..."
                    value={formData.notes}
                    onChange={handleFormChange}
                    disabled={uploading}
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">These notes will be displayed under the video for students</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {selectedMaterialType === 'video' ? 'Video File or YouTube URL *' : 'Upload File *'}
                </label>
                {selectedMaterialType === 'video' ? (
                  <div className="space-y-2">
                    <input 
                      type="file" 
                      name="file"
                      accept="video/*"
                      className="input-field"
                      onChange={handleFormChange}
                      disabled={uploading}
                    />
                    <div className="text-center text-gray-500 text-sm">OR</div>
                    <input 
                      type="url" 
                      name="youtube_url"
                      className="input-field" 
                      placeholder="https://youtube.com/watch?v=..."
                      value={formData.youtube_url}
                      onChange={handleFormChange}
                      disabled={uploading}
                    />
                  </div>
                ) : (
                  <input 
                    type="file" 
                    name="file"
                    className="input-field"
                    onChange={handleFormChange}
                    required={selectedMaterialType !== 'video'}
                    disabled={uploading}
                  />
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
                  <input 
                    type="number" 
                    name="order"
                    className="input-field" 
                    placeholder="1" 
                    min="1"
                    value={formData.order}
                    onChange={handleFormChange}
                    disabled={uploading}
                  />
                </div>
                <div>
                  <label className="flex items-center space-x-2">
                    <input 
                      type="checkbox" 
                      name="is_public"
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      checked={formData.is_public}
                      onChange={handleFormChange}
                      disabled={uploading}
                    />
                    <span className="text-sm font-medium text-gray-700">Make Public</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1">Allow access without login</p>
                </div>
              </div>
              
              <div className="flex space-x-3 pt-4">
                <button 
                  type="submit" 
                  className="flex-1 btn-primary disabled:opacity-50"
                  disabled={uploading}
                >
                  {uploading ? 'Uploading...' : 'Upload Material'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="flex-1 btn-secondary"
                  disabled={uploading}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialManagement; 